import type { BillingCurrency, StripePriceDto, WorkspaceUsageDto } from '@/features/billing/types';

// Repli EUR tant que le back ne renvoie pas la devise du workspace.
export function resolveBillingCurrency(currency: string | null | undefined): BillingCurrency {
  return currency?.toUpperCase() === 'USD' ? 'USD' : 'EUR';
}

export function resolvePriceCurrency(
  prices: StripePriceDto[] | null | undefined,
  priceId: string | null | undefined,
  workspaceCurrency: string | null | undefined,
): string {
  const price = priceId ? prices?.find((candidate) => candidate.id === priceId) : undefined;
  return price?.currency || resolveBillingCurrency(workspaceCurrency);
}

function getCurrencySymbol(currency: string): string {
  switch (currency?.toUpperCase()) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    default:
      return currency ? `${currency} ` : '€';
  }
}

export function isPaymentOverdueStatus(status: string | null | undefined): boolean {
  return status === 'past_due' || status === 'unpaid';
}

// Statut Stripe brut : `trialing` est un abonnement en cours de période d'essai, il donne accès au produit.
export function isEntitledSubscriptionStatus(status: string | null | undefined): boolean {
  return status === 'active' || status === 'trialing';
}

export function formatPrice(amount: number, currency: string = 'USD'): string {
  const hasCents = amount % 100 !== 0;
  const value = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: hasCents ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(amount / 100);
  return `${value}${getCurrencySymbol(currency)}`;
}

// Le forfait annuel est présenté au tarif mensuel équivalent : on arrondit au centime
// pour éviter les fractions de centime quand le tarif annuel n'est pas divisible par 12.
export function getMonthlyEquivalentAmount(yearlyAmount: number): number {
  return Math.round(yearlyAmount / 12);
}

export function getPriceByInterval(
  prices: StripePriceDto[] | null,
  nickname: string,
  interval: 'month' | 'year',
): StripePriceDto | null {
  if (!prices?.length) return null;
  return (
    prices.find(
      (p) => p.interval === interval && p.nickname?.toLowerCase() === nickname.toLowerCase(),
    ) ?? null
  );
}

export function getMonthlyPriceForPlan(
  prices: StripePriceDto[] | null,
  nickname: string,
): string | null {
  const monthly = getPriceByInterval(prices, nickname, 'month');
  return monthly ? formatPrice(monthly.amount, monthly.currency) : null;
}

export function getPricingInfo(
  prices: StripePriceDto[] | null,
  nickname: string,
): { monthlyAmount: number; yearlyAmount: number; savings: number } | null {
  const monthly = getPriceByInterval(prices, nickname, 'month');
  const yearly = getPriceByInterval(prices, nickname, 'year');
  if (!monthly) return null;
  // Les économies comparent deux tarifs reconduits : une promo limitée à la 1re facture ne doit pas les gonfler.
  const monthlyAmount = monthly.renewalAmount ?? monthly.discountedAmount;
  const yearlyAmount = yearly
    ? (yearly.renewalAmount ?? yearly.discountedAmount)
    : monthlyAmount * 12;
  const savings = Math.max(0, monthlyAmount * 12 - yearlyAmount);
  return { monthlyAmount, yearlyAmount, savings };
}

export const PLAN_ADVANTAGE_COUNT = 7;

export type Plan = {
  name: string;
  priceKey: string;
  img: string;
  /** Index des avantages absents du forfait : la grille les affiche barrés au lieu de les masquer. */
  excludedAdvantages: number[];
  highlighted: boolean;
};

export const plans: Plan[] = [
  {
    name: 'Starter',
    priceKey: 'Starter',
    img: '/assets/plan_launch.webp',
    excludedAdvantages: [],
    highlighted: false,
  },
  {
    name: 'Pro',
    priceKey: 'Pro',
    img: '/assets/plan_grow.webp',
    excludedAdvantages: [],
    highlighted: true,
  },
  {
    name: 'Entreprise',
    priceKey: 'Enterprise',
    img: '/assets/plan_scale.webp',
    excludedAdvantages: [],
    highlighted: false,
  },
];

/**
 * Le back continue de nommer les forfaits Starter/Pro/Enterprise (clés Stripe inchangées) alors que
 * l'application les présente sous leurs noms commerciaux. Sans cette table, l'abonnement en cours
 * afficherait « Enterprise » pendant que la grille annonce « Scale ».
 */
const PLAN_DISPLAY_NAMES: Record<string, string> = {
  starter: 'Launch',
  pro: 'Grow',
  enterprise: 'Scale',
  entreprise: 'Scale',
};

/**
 * Scale accueille autant de spectateurs que nécessaire : le compteur peut dépasser `participants.max`
 * sans que la salle soit pleine pour autant, la bannière ne doit donc jamais s'afficher dans ce cas.
 */
export function isLiveRoomFull(
  usage: Pick<WorkspaceUsageDto, 'participants' | 'participantsUnlimited'> | null | undefined,
  liveParticipantCount: number,
): boolean {
  if (!usage?.participants || usage.participantsUnlimited) return false;
  return liveParticipantCount >= usage.participants.max;
}

export function getPlanDisplayName(planName: string | null | undefined): string {
  if (!planName) return '';
  return PLAN_DISPLAY_NAMES[planName.trim().toLowerCase()] ?? planName;
}

/**
 * Le back peut ne renvoyer qu'une partie du catalogue (offre partenaire limitée à Entreprise).
 * Une carte sans tarif reste bloquée sur « chargement des prix » et son bouton mène à un
 * paiement impossible : on n'affiche que les forfaits réellement achetables.
 * Liste vide ou absente = tarifs pas encore chargés, on garde le catalogue complet.
 */
export function getPurchasablePlans(prices: StripePriceDto[] | null | undefined): Plan[] {
  if (!prices?.length) return plans;
  const purchasable = plans.filter(
    (plan) => getPriceByInterval(prices, plan.priceKey, 'month') != null,
  );
  return purchasable.length > 0 ? purchasable : plans;
}

export function isPartnerAccountOffer(prices: StripePriceDto[] | null | undefined): boolean {
  return (prices ?? []).some((price) => price.partnerAccountOfferApplied === true);
}

export function getPlanTierIndex(planKeyOrName: string | null | undefined): number | null {
  if (!planKeyOrName) return null;
  const normalized = planKeyOrName.trim().toLowerCase();
  const idx = plans.findIndex(
    (p) => p.priceKey.toLowerCase() === normalized || p.name.toLowerCase() === normalized,
  );
  return idx >= 0 ? idx : null;
}

export function isPlanDowngrade(
  currentPlanName: string | null | undefined,
  selectedPlanKey: string,
): boolean {
  const currentIdx = getPlanTierIndex(currentPlanName);
  const selectedIdx = getPlanTierIndex(selectedPlanKey);
  if (currentIdx == null || selectedIdx == null) return false;
  return selectedIdx < currentIdx;
}
